import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Grid,
  InputAdornment,
  Divider,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  Person,
  Phone,
  WhatsApp,
  Email,
  Work,
  Close,
  CloudUpload,
  Save,
  CreditCard,
  Home,
  AccountBalance,
  ArrowBack,
} from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { customerService } from '../../services/customer';
import { getSignedUrls, uploadFileToS3, viewUrl } from '../../services/s3';
import AlertModal from '../../components/AlertModal';

const CustomerForm = () => {
  const history = useHistory();
  const location = useLocation();
  const customerData = location.state?.customer || null;
  const isEditMode = !!customerData;

  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const showAlert = (message, title = 'Alert') => {
    setAlertModal({ open: true, title, message });
  };
  const closeAlert = () => {
    setAlertModal({ open: false, title: 'Alert', message: '' });
  };

  const [uploadedImage, setUploadedImage] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [phoneVerificationError, setPhoneVerificationError] = useState('');
  const [associates, setAssociates] = useState([]);
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [panError, setPanError] = useState('');
  const [aadharError, setAadharError] = useState('');
  const [professionalOptions, setProfessionalOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [addCityOpen, setAddCityOpen] = useState(false);
  const [addCityName, setAddCityName] = useState('');
  const [addCityError, setAddCityError] = useState('');
  const [savingCity, setSavingCity] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [whatsappError, setWhatsappError] = useState('');

  const validateMobile = (mobile) => {
    if (!mobile) return false;
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(mobile);
  };

  // Form data state - handle both flat and nested API structures
  const [formData, setFormData] = useState({
    fullName: customerData?.name || '',
    mobile: customerData?.mobile || customerData?.phone || '',
    whatsappNumber: customerData?.whatsappNumber || customerData?.whatsapp || '',
    email: customerData?.email || '',
    company: customerData?.company || '',
    registerDate: customerData?.joinDate || new Date().toISOString().split('T')[0],
    aadahr: customerData?.aadahr || customerData?.personalDetails?.aadahr || '',
    panNumber: customerData?.panNumber || customerData?.personalDetails?.pan || '',
    fatherName: customerData?.fatherName || customerData?.basicDetails?.fatherName || '',
    imgUrl: customerData?.imgUrl || customerData?.image || '',
    fullAddress: customerData?.address || customerData?.otherDetails?.address || '',
    state: customerData?.state || '',
    city: customerData?.city || '',
    pinCode: customerData?.pinCode || customerData?.pinCode || '',
    dateOfBirth: customerData?.dateOfBirth || customerData?.personalDetails?.dob || '',
    age: customerData?.age || customerData?.personalDetails?.age || '',
    anniversary: customerData?.anniversary || customerData?.personalDetails?.anniversary || '',
    maritalStatus: customerData?.maritalStatus || customerData?.personalDetails?.maritalStatus || '',
    gender: customerData?.gender || customerData?.basicDetails?.gender || '',
    professionalName: customerData?.profession || customerData?.professionalName || '',
    remark: customerData?.remark || customerData?.otherDetails?.remark || '',
    bloodGroup: customerData?.bloodGroup || customerData?.basicDetails?.bloodGroup || '',
    associateName: customerData?.associateName || '',
    assignedTo: customerData?.assignedTo || null,
    salaryRange: customerData?.salaryRange || customerData?.otherDetails?.salaryRange || '',
    source: customerData?.propertyInterest || customerData?.otherDetails?.createdDetail || '',
    createdBy: customerData?.createdBy || 'a1 Admin',
  });

  // Handle existing image in edit mode - fetch signed URL if it's an S3 key or S3 URL
  useEffect(() => {
    const loadExistingImage = async () => {
      // Check for imgUrl first (from API response), then fallback to image
      const imageUrl = customerData?.imgUrl || customerData?.image;

      if (isEditMode && imageUrl && imageUrl.trim() !== '') {
        setLoadingImage(true);
        try {
          // Use viewUrl to get signed URL - it handles both S3 keys and full S3 URLs
          const signedUrl = await viewUrl(imageUrl);
          if (signedUrl) {
            setUploadedImage(signedUrl);
          } else {
            // If viewUrl returns null, try using the URL directly (might be a public URL or base64)
            setUploadedImage(imageUrl);
          }
        } catch (error) {
          console.error('Failed to load existing image:', error);
          // Fallback: try using the URL directly
          setUploadedImage(imageUrl);
        } finally {
          setLoadingImage(false);
        }
      }
    };
    loadExistingImage();
  }, [isEditMode, customerData?.imgUrl, customerData?.image]);

  useEffect(() => {
    let active = true;
    const loadStates = async () => {
      setLoadingStates(true);
      try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails?type=state`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        const body = await response.json().catch(() => ({}));
        const list = Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.details)
            ? body.details
            : Array.isArray(body)
              ? body
              : [];
        const mapped = list
          .map((item) => {
            const id = item?._id || item?.id;
            const label =
              item?.details?.stateName ||
              item?.stateName ||
              item?.name ||
              item?.value ||
              '';
            if (!id || !label) return null;
            return { id, label };
          })
          .filter(Boolean);
        if (active) {
          setStateOptions(mapped);
          // Attempt to map existing state label to id for edit mode
          if (formData.state && !mapped.some((opt) => opt.id === formData.state)) {
            const match = mapped.find((opt) => opt.label === formData.state);
            if (match) {
              setFormData((prev) => ({ ...prev, state: match.id }));
            }
          }
        }
      } catch (error) {
        console.error('Failed to load states:', error);
        if (active) setStateOptions([]);
      } finally {
        if (active) setLoadingStates(false);
      }
    };
    loadStates();
    return () => {
      active = false;
    };
  }, [formData.state]);

  useEffect(() => {
    let active = true;
    const loadCities = async () => {
      if (!formData.state) {
        setCityOptions([]);
        return;
      }
      setLoadingCities(true);
      try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails?type=city`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        const body = await response.json().catch(() => ({}));
        const list = Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.details)
            ? body.details
            : Array.isArray(body)
              ? body
              : [];

        const selectedStateLabel =
          stateOptions.find((opt) => opt.id === formData.state)?.label ||
          stateOptions.find((opt) => opt.label === formData.state)?.label ||
          '';

        const mapped = list
          .map((item) => {
            const id = item?._id || item?.id;
            const label =
              item?.details?.cityName ||
              item?.cityName ||
              item?.name ||
              item?.value ||
              '';
            const itemState =
              item?.details?.state ||
              item?.state ||
              '';
            if (!id || !label) return null;
            if (selectedStateLabel) {
              if (String(itemState || '').toLowerCase() !== String(selectedStateLabel).toLowerCase()) {
                return null;
              }
            }
            return { id, label };
          })
          .filter(Boolean);
        if (active) {
          setCityOptions(mapped);
          if (formData.city && !mapped.some((opt) => opt.id === formData.city)) {
            const match = mapped.find((opt) => opt.label === formData.city);
            if (match) {
              setFormData((prev) => ({ ...prev, city: match.id }));
            }
          }
        }
      } catch (error) {
        console.error('Failed to load cities:', error);
        if (active) setCityOptions([]);
      } finally {
        if (active) setLoadingCities(false);
      }
    };
    loadCities();
    return () => {
      active = false;
    };
  }, [formData.state, formData.city, stateOptions]);

  useEffect(() => {
    let active = true;
    const loadProfessionals = async () => {
      try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails?type=professional`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }
        );
        const body = await response.json().catch(() => ({}));
        const list = Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.details)
            ? body.details
            : Array.isArray(body)
              ? body
              : [];
        const mapped = list
          .map((item) => {
            if (!item) return null;
            const id = item?._id || item?.id;
            const label =
              item?.details?.professionalName ||
              item?.professionalName ||
              item?.name ||
              item?.value ||
              '';
            if (!id || !label) return null;
            return { id, label };
          })
          .filter(item => item && item.id && item.label); // More strict filtering
        if (active) setProfessionalOptions(mapped);
      } catch (error) {
        console.error('Failed to load professional list:', error);
        if (active) setProfessionalOptions([]);
      }
    };
    loadProfessionals();
    return () => {
      active = false;
    };
  }, []);

  // Auto-set profession ID when professional options are loaded and professionalName exists
  useEffect(() => {
    if (professionalOptions.length > 0 && formData.professionalName) {
      const validOptions = professionalOptions.filter(opt => opt && opt.id && opt.label);
      const matchingOption = validOptions.find(
        (option) => option.label === formData.professionalName
      );
      if (matchingOption && !formData.profession) {
        handleFormChange('profession', matchingOption.id);
      }
    }
  }, [professionalOptions, formData.professionalName]);

  // Fetch associates on component mount
  useEffect(() => {
    const fetchAssociates = async () => {
      setLoadingAssociates(true);
      try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/associates?page=1&limit=100000`,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        const body = await response.json().catch(() => ({}));
        const list = Array.isArray(body?.data) ? body.data : [];
        const mappedAssociates = list.map((associate) => ({
          id: associate._id,
          _id: associate._id,
          name: associate.name || '',
          phone: associate.phone || '',
          displayLabel: `${associate.name || ''} - ${associate.phone || ''}`,
        }));
        setAssociates(mappedAssociates);

        if (isEditMode && customerData?.assignedTo) {
          const foundAssociate = mappedAssociates.find((a) => a._id === customerData.assignedTo);
          if (foundAssociate) {
            setSelectedAssociate(foundAssociate);
          }
        }
      } catch (error) {
        console.error('Failed to load associates:', error);
        setAssociates([]);
      } finally {
        setLoadingAssociates(false);
      }
    };

    fetchAssociates();
  }, [isEditMode, customerData?.assignedTo]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validatePanNumber = (pan) => {
    if (!pan) return '';

    // PAN format: AAAAA9999A (5 letters, 4 digits, 1 letter)
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

    if (pan.length !== 10) {
      return 'PAN must be exactly 10 characters';
    }

    if (!panRegex.test(pan)) {
      return 'Invalid PAN format. Must be AAAAA9999A (5 letters, 4 digits, 1 letter)';
    }

    return '';
  };

  const validateAadharNumber = (aadhar) => {
    if (!aadhar) return '';
    const cleanAadhar = aadhar.replace(/\s/g, ''); // Remove spaces
    const aadharRegex = /^\d{12}$/; // Exactly 12 digits

    if (cleanAadhar.length !== 12) {
      return 'Aadhar must be exactly 12 digits';
    }
    if (!aadharRegex.test(cleanAadhar)) {
      return 'Invalid Aadhar format. Must contain only digits';
    }
    return ''; // Valid
  };

  const handleMobileChange = (value) => {
    // Only allow digits and limit to 10 digits
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);

    setFormData((prev) => {
      const newForm = { ...prev, mobile: digitsOnly };

      // Auto-fill whatsappNumber with mobile value if whatsappNumber is empty or same as previous mobile
      if (!prev.whatsappNumber || prev.whatsappNumber === prev.mobile) {
        newForm.whatsappNumber = digitsOnly;
      }

      return newForm;
    });

    if (digitsOnly && digitsOnly.length < 10) {
      setMobileError('Mobile number must be 10 digits');
    } else if (digitsOnly && !validateMobile(digitsOnly)) {
      setMobileError('Mobile number invalid');
    } else {
      setMobileError('');
    }

    // Also validate WhatsApp if it was auto-filled
    setFormData((prev) => {
      if (!prev.whatsappNumber || prev.whatsappNumber === digitsOnly) {
        if (digitsOnly && digitsOnly.length < 10) {
          setWhatsappError('WhatsApp number must be 10 digits');
        } else if (digitsOnly && !validateMobile(digitsOnly)) {
          setWhatsappError('Mobile number invalid');
        } else {
          setWhatsappError('');
        }
      }
      return prev;
    });
  };

  const handleOpenAddCity = () => {
    setAddCityName('');
    setAddCityError('');
    setAddCityOpen(true);
  };

  const handleSaveCity = async () => {
    setAddCityError('');
    if (!formData.state) {
      setAddCityError('Please select a state first.');
      return;
    }
    if (!addCityName.trim()) {
      setAddCityError('City name is required.');
      return;
    }

    const stateLabel =
      stateOptions.find((opt) => opt.id === formData.state)?.label ||
      stateOptions.find((opt) => opt.label === formData.state)?.label ||
      '';

    try {
      setSavingCity(true);
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          type: 'city',
          details: { cityName: addCityName.trim(), state: stateLabel },
          status: true,
        }),
      });
      const body = await response.json().catch(() => ({}));
      const ok = body?.success === true || response.ok;
      if (!ok) {
        throw new Error(body?.message || 'Failed to add city.');
      }
      const newId = body?.data?._id || body?.data?.id;
      const newLabel = body?.data?.details?.cityName || addCityName.trim();
      const newCity = { id: newId, label: newLabel };
      setCityOptions((prev) => {
        const exists = prev.some((c) => c.id === newId);
        return exists ? prev : [...prev, newCity];
      });
      if (newId) {
        setFormData((prev) => ({ ...prev, city: newId }));
      }
      setAddCityOpen(false);
      setAddCityName('');
    } catch (error) {
      console.error('Failed to add city:', error);
      setAddCityError(error.message || 'Failed to add city.');
    } finally {
      setSavingCity(false);
    }
  };

  const handlePhoneVerification = async (phoneNumber) => {
    // Only verify if phone number is exactly 10 digits
    if (!phoneNumber || phoneNumber.length !== 10 || !/^\d{10}$/.test(phoneNumber)) {
      return;
    }

    setVerifyingPhone(true);
    setPhoneVerificationError('');

    try {
      const customerData = await customerService.verifyCustomerByPhone(phoneNumber);

      if (customerData) {
        // Auto-fill form fields with verified customer data
        setFormData((prev) => ({
          ...prev,
          fullName: customerData.name || prev.fullName,
          company: customerData.company || prev.company,
          professionalName: customerData.profession || prev.professionalName,
          email: customerData.email || prev.email,
          whatsappNumber: customerData.whatsapp || prev.whatsappNumber,
          pinCode: customerData.pinCode || prev.pinCode,
        }));

        // Show success message
      }
    } catch (error) {
      console.error('Phone verification failed:', error);
      showAlert('Phone verification failed. Please enter details manually.', 'Error');
    } finally {
      setVerifyingPhone(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setImageUploadError('Please select a valid image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setImageUploadError('Image size must be less than 10MB');
      return;
    }

    setUploadingImage(true);
    setImageUploadError('');

    try {
      // Create a preview using FileReader for immediate display
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result);
      };
      reader.readAsDataURL(file);

      // Get file extension
      const fileName = file.name;
      const fileExtension = fileName.split('.').pop() || 'jpg';
      const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
      const timestamp = Date.now();
      const finalFileName = `customer/${timestamp}_${sanitizedFileName}`;

      // Get signed URL for upload
      const signedUrls = await getSignedUrls([
        {
          fileName: finalFileName,
          fileType: file.type || 'image/jpeg',
        },
      ]);

      if (!signedUrls || signedUrls.length === 0) {
        throw new Error('Failed to get signed URL for upload');
      }

      const signedUrlInfo = signedUrls[0];
      if (!signedUrlInfo || !signedUrlInfo.uploadUrl) {
        throw new Error('Invalid signed URL response');
      }

      // Upload file to S3
      const uploadSuccess = await uploadFileToS3(
        signedUrlInfo.uploadUrl,
        file,
        file.type || 'image/jpeg'
      );

      if (!uploadSuccess) {
        throw new Error('Failed to upload file to S3');
      }

      // Extract full S3 URL (remove query parameters)
      const s3Url = signedUrlInfo.uploadUrl.split('?')[0];

      // Verify we have a full URL
      if (!s3Url || (!s3Url.startsWith('http://') && !s3Url.startsWith('https://'))) {
        throw new Error('Invalid S3 URL generated');
      }

      // Update form data with S3 URL
      setFormData((prev) => ({ ...prev, imgUrl: s3Url }));
    } catch (error) {
      console.error('Failed to upload image:', error);
      setImageUploadError(error.message || 'Failed to upload image. Please try again.');
      setUploadedImage(null);
      setFormData((prev) => ({ ...prev, imgUrl: '' }));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFormSubmit = async () => {

    if (!formData.fullName.trim() || !formData.mobile.trim() || !formData.email.trim()) {
      showAlert('Please fill in all required fields (Full Name, Mobile, Email).', 'Error');
      return;
    }

    if (!validateMobile(formData.mobile)) {
      setMobileError('Mobile number invalid');
      showAlert('Please enter a valid 10-digit mobile number starting with 6-9.', 'Error');
      return;
    }

    if (formData.whatsappNumber && !validateMobile(formData.whatsappNumber)) {
      setWhatsappError('Mobile number invalid');
      showAlert('Please enter a valid 10-digit WhatsApp number starting with 6-9.', 'Error');
      return;
    }

    // Validate PAN number if provided
    if (formData.panNumber && validatePanNumber(formData.panNumber)) {
      showAlert(validatePanNumber(formData.panNumber), 'Error');
      return;
    }

    // Validate Aadhar number if provided
    if (formData.aadahr && validateAadharNumber(formData.aadahr)) {
      showAlert(validateAadharNumber(formData.aadahr), 'Error');
      return;
    }

    const payload = {
      name: formData.fullName.trim(),
      profession: formData.professionalName.trim(),
      phone: formData.mobile.trim(),
      whatsapp: formData.whatsappNumber.trim(),
      email: formData.email.trim(),
      company: formData.company.trim(),
      city: selectedCityLabel,
      state: selectedStateLabel,
      pinCode: formData.pinCode.trim(),
      ...(formData.imgUrl && { imgUrl: formData.imgUrl }),
      basicDetails: {
        gender: formData.gender,
        fatherName: formData.fatherName.trim(),
        bloodGroup: formData.bloodGroup,
      },
      personalDetails: {
        dob: formData.dateOfBirth,
        age: formData.age,
        anniversary: formData.anniversary,
        maritalStatus: formData.maritalStatus,
        pan: formData.panNumber.trim(),
        aadahr: formData.aadahr.trim(),
      },
      otherDetails: {
        address: formData.fullAddress.trim(),
        // createdDetail: formData.source.trim(),
        remark: formData.remark.trim(),
        // salaryRange: formData.salaryRange.trim(),
      },
      associateName: formData.associateName.trim(),
      ...(formData.assignedTo && { assignedTo: formData.assignedTo }),
      createdBy: formData.createdBy.trim() || 'a1 Admin',
    };

    try {
      setSubmitting(true);
      if (isEditMode) {
        const customerId = customerData?.id || customerData?._id;
        await customerService.updateCustomer(customerId, payload);
        showAlert('Customer updated successfully!', 'Success');
      } else {
        await customerService.createCustomer(payload);
        showAlert('Customer added successfully!', 'Success');
      }
      setTimeout(() => {
        history.push('/app/members/customer');
      }, 1500);
    } catch (err) {
      console.error('Failed to create customer:', err);
      showAlert(err.message || 'Failed to save customer. Please try again.', 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    history.push('/app/members/customer');
  };

  const handleCloseAddCity = () => {
    setAddCityOpen(false);
    setAddCityError('');
    setAddCityName('');
  };

  const selectedStateLabel =
    stateOptions.find((opt) => opt.id === formData.state)?.label ||
    stateOptions.find((opt) => opt.label === formData.state)?.label ||
    '';

  const selectedCityLabel =
    cityOptions.find((opt) => opt.id === formData.city)?.label ||
    cityOptions.find((opt) => opt.label === formData.city)?.label ||
    '';

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Form Page Header */}
      <Box sx={{ mb: 4, pb: 3, borderBottom: '2px solid #e0e0e0', backgroundColor: 'white', p: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              {isEditMode ? 'Edit Customer' : 'Add New Customer'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditMode ? `Update customer information for ${customerData?.customerId}` : 'Fill in the details to add a new customer'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleCancel}
            sx={{ textTransform: 'none', px: 3 }}
          >
            Back to Customer List
          </Button>
        </Box>
      </Box>

      {/* Form Content */}
      <Paper sx={{ p: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Grid container spacing={3}>
          {/* Photo Upload Section */}
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              {loadingImage ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={60} />
                  <Typography variant="caption" color="text.secondary">
                    Loading image...
                  </Typography>
                </Box>
              ) : (
                <Avatar
                  src={uploadedImage}
                  sx={{
                    width: 120,
                    height: 120,
                    margin: '0 auto',
                    border: '3px solid #536DFE',
                    mb: 2
                  }}
                >
                  {formData.fullName ? formData.fullName.charAt(0) : <Person />}
                </Avatar>
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={uploadingImage ? <CircularProgress size={18} /> : <CloudUpload />}
                disabled={uploadingImage}
                sx={{ textTransform: 'none' }}
              >
                {uploadingImage ? 'Uploading...' : 'Upload Photo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                />
              </Button>
              {imageUploadError && (
                <Alert severity="error" sx={{ mt: 2, width: '100%' }} onClose={() => setImageUploadError('')}>
                  {imageUploadError}
                </Alert>
              )}
            </Box>
            <Divider sx={{ my: 3 }} />
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              required
              label="Full Name"
              value={formData.fullName}
              onChange={(e) => handleFormChange('fullName', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Father's Name"
              value={formData.fatherName}
              onChange={(e) => handleFormChange('fatherName', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={formData.gender}
                onChange={(e) => handleFormChange('gender', e.target.value)}
                label="Gender"
              >
                <MenuItem value="">
                  <em>Select Gender</em>
                </MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Profession</InputLabel>
              <Select
                value={formData.profession || ''}
                // onChange={(e) => handleFormChange('profession', e.target.value)}
                label="Profession"
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  handleFormChange('profession', selectedValue);
                  // Also update professionalName with the selected option's label
                  const selectedOption = professionalOptions.find(opt => opt.id === selectedValue);
                  handleFormChange('professionalName', selectedOption ? selectedOption.label : '');
                }}
              >
                <MenuItem value="">Select Profession</MenuItem>
                {professionalOptions.filter(opt => opt && opt.id && opt.label).map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
                {formData.professionalName &&
                  !professionalOptions.filter(opt => opt && opt.id && opt.label).some(
                    (opt) => opt.id === formData.profession || opt.label === formData.profession
                  ) && (
                    <MenuItem value={formData.professionalName}>{formData.professionalName}</MenuItem>
                  )}
              </Select>
            </FormControl>
          </Grid>

          {/* <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Company"
              value={formData.company}
              onChange={(e) => handleFormChange('company', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Work color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid> */}

          {/* <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Blood Group</InputLabel>
              <Select
                value={formData.bloodGroup}
                onChange={(e) => handleFormChange('bloodGroup', e.target.value)}
                label="Blood Group"
              >
                <MenuItem value="A+">A+</MenuItem>
                <MenuItem value="A-">A-</MenuItem>
                <MenuItem value="B+">B+</MenuItem>
                <MenuItem value="B-">B-</MenuItem>
                <MenuItem value="AB+">AB+</MenuItem>
                <MenuItem value="AB-">AB-</MenuItem>
                <MenuItem value="O+">O+</MenuItem>
                <MenuItem value="O-">O-</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Date of Birth"
              value={formData.dateOfBirth}
              onChange={(e) => handleFormChange('dateOfBirth', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: (() => {
                  const date = new Date();
                  date.setFullYear(date.getFullYear() - 10);
                  return date.toISOString().split('T')[0];
                })(), // Restrict to dates at least 10 years ago
              }}

            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Marital Status</InputLabel>
              <Select
                value={formData.maritalStatus || ''}
                onChange={(e) => handleFormChange('maritalStatus', e.target.value)}
                label="Marital Status"
              >
                <MenuItem value="">
                  <em>Select Marital Status</em>
                </MenuItem>
                <MenuItem value="Single">Single</MenuItem>
                <MenuItem value="Married">Married</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {(formData.maritalStatus === 'Married' || formData.maritalStatus === 'Marriage') && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Anniversary"
                value={formData.anniversary}
                onChange={(e) => handleFormChange('anniversary', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0], // Prevent future dates
                }}
                helperText="Select current or past date only"
              />
            </Grid>
          )}

          {/* Contact Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Contact Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Mobile Number"
              value={formData.mobile}
              onChange={(e) => handleMobileChange(e.target.value)}
              onBlur={(e) => handlePhoneVerification(e.target.value)}
              inputProps={{
                maxLength: 10,
                pattern: '[0-9]*',
                inputMode: 'numeric',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                ),
                endAdornment: verifyingPhone ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
              }}
              helperText={mobileError || phoneVerificationError || "Enter 10 digit mobile number"}
              error={!!mobileError || !!phoneVerificationError}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="WhatsApp Number"
              value={formData.whatsappNumber}
              onChange={(e) => {
                // Only allow digits and limit to 10 digits
                const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                handleFormChange('whatsappNumber', digitsOnly);
                if (digitsOnly && digitsOnly.length < 10) {
                  setWhatsappError('WhatsApp number must be 10 digits');
                } else if (digitsOnly && !validateMobile(digitsOnly)) {
                  setWhatsappError('Mobile number invalid');
                } else {
                  setWhatsappError('');
                }
              }}
              inputProps={{
                maxLength: 10,
                pattern: '[0-9]*',
                inputMode: 'numeric',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WhatsApp color="success" />
                  </InputAdornment>
                ),
              }}
              helperText={whatsappError || "Enter 10 digit WhatsApp number (auto-filled from mobile)"}
              error={!!whatsappError}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              type="email"
              label="Email ID"
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Address Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Full Address"
              value={formData.fullAddress}
              onChange={(e) => handleFormChange('fullAddress', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Home color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>State</InputLabel>
              <Select
                value={formData.state}
                label="State"
                onChange={(e) => {
                  handleFormChange('state', e.target.value);
                  handleFormChange('city', '');
                }}
                disabled={loadingStates}
              >
                <MenuItem value="">
                  <em>Select State</em>
                </MenuItem>
                {stateOptions.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth disabled={!formData.state || loadingCities}>
              <InputLabel>City</InputLabel>
              <Select
                value={formData.city}
                label="City"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '__add_city__') {
                    handleOpenAddCity();
                    return;
                  }
                  handleFormChange('city', value);
                }}
              >
                <MenuItem value="">
                  <em>Select City</em>
                </MenuItem>
                <MenuItem value="__add_city__" disabled={!formData.state}>
                  + Add New City
                </MenuItem>
                {cityOptions.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Pin Code"
              value={formData.pinCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                handleFormChange('pinCode', value);
              }}
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric'
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {/* <CreditCard color="action" /> */}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Associate Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Associate Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              options={associates}
              getOptionLabel={(option) => option.displayLabel || `${option.name} - ${option.phone}`}
              value={selectedAssociate}
              onChange={(event, newValue) => {
                setSelectedAssociate(newValue);
                if (newValue) {
                  setFormData((prev) => ({
                    ...prev,
                    associateName: newValue.name || '',
                    assignedTo: newValue._id || newValue.id || null,
                  }));
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    associateName: '',
                    assignedTo: null,
                  }));
                }
              }}
              loading={loadingAssociates}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Associate Name"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {loadingAssociates ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {option.phone}
                  </Typography>
                </Box>
              )}
            />
          </Grid>

          {/* <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Salary Range"
              value={formData.salaryRange}
              onChange={(e) => handleFormChange('salaryRange', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountBalance color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid> */}

          {/* <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Lead Source"
              value={formData.source}
              onChange={(e) => handleFormChange('source', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Work color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid> */}

          {/* Document Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Document Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Aadhar Number"
              value={formData.aadahr}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                handleFormChange('aadahr', value);
                setAadharError(validateAadharNumber(value));
              }}
              onBlur={(e) => setAadharError(validateAadharNumber(formData.aadahr))}
              error={!!aadharError}
              helperText={aadharError || "Enter exactly 12-digit numbers"}
              inputProps={{ maxLength: 12 }}
              placeholder="XXXX-XXXX-XXXX"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CreditCard color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="PAN Number"
              value={formData.panNumber}
              onChange={(e) => {
                const upperValue = e.target.value.toUpperCase();
                handleFormChange('panNumber', upperValue);
                setPanError(validatePanNumber(upperValue));
              }}
              onBlur={(e) => {
                setPanError(validatePanNumber(e.target.value));
              }}
              placeholder="ABCDE1234F"
              inputProps={{ maxLength: 10 }}
              error={!!panError}
              helperText={panError || "Format: AAAAA9999A (5 letters, 4 digits, 1 letter)"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountBalance color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Administrative Details */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Other Details
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Created By"
              value={formData.createdBy}
              onChange={(e) => handleFormChange('createdBy', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remark"
              value={formData.remark}
              onChange={(e) => handleFormChange('remark', e.target.value)}
              placeholder="Add any additional notes or remarks..."
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                startIcon={<Close />}
                sx={{ textTransform: 'none', px: 4, py: 1.2 }}
                size="large"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFormSubmit}
                variant="contained"
                color="primary"
                disabled={submitting}
                startIcon={
                  submitting ? <CircularProgress size={20} color="inherit" /> : <Save />
                }
                sx={{
                  textTransform: 'none',
                  px: 4,
                  py: 1.2,
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)'
                }}
                size="large"
              >
                {submitting ? 'Saving...' : isEditMode ? 'Update Customer' : 'Add Customer'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={addCityOpen} onClose={handleCloseAddCity} maxWidth="sm" fullWidth>
        <DialogTitle>Add New City</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="State"
                value={selectedStateLabel || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="City Name"
                value={addCityName}
                onChange={(e) => setAddCityName(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            {addCityError && (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setAddCityError('')}>
                  {addCityError}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCity}>Cancel</Button>
          <Button onClick={handleSaveCity} variant="contained" disabled={savingCity}>
            {savingCity ? 'Saving...' : 'Add City'}
          </Button>
        </DialogActions>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default CustomerForm;
